import React, { useState, useEffect } from 'react';
import { X, HeartPulse, Scale, Moon, Activity } from 'lucide-react';
import { formatLocalDate } from '../utils/date';

const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

const DailyHealthModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  // Form fields
  const [date, setDate] = useState(formatLocalDate(new Date()));
  const [weight, setWeight] = useState('');
  const [sleepDuration, setSleepDuration] = useState('');
  const [rhr, setRhr] = useState('');
  const [systolic, setSystolic] = useState('');
  const [diastolic, setDiastolic] = useState('');
  const [showClinical, setShowClinical] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDate(formatLocalDate(new Date()));
      setWeight('');
      setSleepDuration('');
      setRhr('');
      setSystolic('');
      setDiastolic('');
      setMessage('');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    // Remove empty values
    const payload = { date };
    if (weight) payload.weight = weight;
    if (sleepDuration) payload.sleepDuration = sleepDuration;
    if (rhr) payload.rhr = rhr;
    if (systolic) payload.systolic = systolic;
    if (diastolic) payload.diastolic = diastolic;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/user/daily-health`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al guardar los datos');
      
      setMessage('Datos guardados correctamente. Recarga la página para ver los cambios en las gráficas.');
      setTimeout(() => {
        onClose();
        // Optional: window.location.reload();
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(5px)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100,
      padding: '1rem'
    }}>
      <div className="glass-panel" style={{
        width: '100%', maxWidth: '450px',
        padding: '2rem', display: 'flex', flexDirection: 'column', position: 'relative'
      }}>
        <button onClick={onClose} style={{
          position: 'absolute', top: '1.5rem', right: '1.5rem',
          background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer',
          padding: '0.5rem', borderRadius: '50%', transition: 'all 0.2s ease'
        }} onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
           onMouseOut={e => e.currentTarget.style.background = 'none'}>
          <X size={20} />
        </button>

        <h2 className="text-xl" style={{ margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <HeartPulse size={24} color="var(--health-cyan)" /> Registro Diario
        </h2>
        <p className="text-muted text-sm" style={{ marginBottom: '2rem' }}>
          Anota tus métricas de salud de hoy si no dispones de sincronización automática.
        </p>

        {message && <div style={{ padding: '0.8rem', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', borderRadius: '8px', marginBottom: '1.5rem', fontSize: '0.9rem' }}>{message}</div>}
        {error && <div style={{ padding: '0.8rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '8px', marginBottom: '1.5rem', fontSize: '0.9rem' }}>{error}</div>}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Fecha</label>
            <input 
              type="date" value={date} onChange={e => setDate(e.target.value)} required
              style={{ width: '100%', padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'white', outline: 'none' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                <Scale size={14} /> Peso (kg)
              </label>
              <input 
                type="number" step="0.1" placeholder="Ej: 75.5" value={weight} onChange={e => setWeight(e.target.value)}
                style={{ width: '100%', padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'white', outline: 'none' }}
              />
            </div>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                <Moon size={14} /> Sueño (horas)
              </label>
              <input 
                type="number" step="0.1" placeholder="Ej: 7.5" value={sleepDuration} onChange={e => setSleepDuration(e.target.value)}
                style={{ width: '100%', padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'white', outline: 'none' }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                <Activity size={14} color="var(--health-cyan)" /> Pulso en Reposo (ppm)
              </label>
              <input 
                type="number" placeholder="Ej: 52" value={rhr} onChange={e => setRhr(e.target.value)}
                style={{ width: '100%', padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'white', outline: 'none' }}
              />
            </div>
          </div>

          {/* CAMPOS CLÍNICOS OPCIONALES (COLAPSADOS POR DEFECTO PARA REDUCIR FRICCIÓN) */}
          <div style={{ borderTop: '1px solid var(--glass-border)', paddingTop: '0.85rem' }}>
            <button
              type="button"
              onClick={() => setShowClinical(!showClinical)}
              style={{
                background: 'none', border: 'none', color: 'var(--text-secondary)',
                fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                padding: 0, fontWeight: 600
              }}
            >
              <span>{showClinical ? '▼ Ocultar' : '▶ Añadir'} datos clínicos avanzados (Presión Arterial opcional)</span>
            </button>

            {showClinical && (
              <div style={{ marginTop: '0.85rem', animation: 'fadeIn 0.2s ease-out' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                  <HeartPulse size={13} /> Tensión Arterial (Sistólica / Diastólica)
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input 
                    type="number" placeholder="120" value={systolic} onChange={e => setSystolic(e.target.value)}
                    style={{ flex: 1, padding: '0.7rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'white', outline: 'none', fontSize: '0.85rem' }}
                  />
                  <span style={{ color: 'var(--text-muted)', fontWeight: 'bold' }}>/</span>
                  <input 
                    type="number" placeholder="80" value={diastolic} onChange={e => setDiastolic(e.target.value)}
                    style={{ flex: 1, padding: '0.7rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'white', outline: 'none', fontSize: '0.85rem' }}
                  />
                </div>
              </div>
            )}
          </div>

          <button type="submit" disabled={loading} style={{
            padding: '1rem', background: 'var(--health-cyan)', color: 'black', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer', marginTop: '0.5rem',
            transition: 'opacity 0.2s ease'
          }} onMouseOver={e => e.currentTarget.style.opacity = 0.9} onMouseOut={e => e.currentTarget.style.opacity = 1}>
            {loading ? 'Guardando...' : 'Guardar Salud de Hoy'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default DailyHealthModal;
